/**
 * Lightweight pointer-driven parallax offset controller.
 *
 * Listens to `pointermove` on a stage element and (optionally)
 * `deviceorientation` on mobile. Each registered layer is translated
 * by `(-nx * depth * MAX_PX, -ny * depth * MAX_PX)` where nx/ny are
 * normalised cursor offsets from the centre of the stage.
 *
 * Smoothing is applied via lerp at 0.12 per frame using rAF.
 */

/** Maximum pixel displacement at depth=1. */
export const MAX_PX = 60;

/** Lerp factor per frame (~0.12). */
const LERP = 0.12;

/** Threshold below which we snap to target (avoids sub-pixel jitter). */
const SNAP = 0.05;

export interface ParallaxLayer {
  el: HTMLElement;
  depth: number;
}

interface Vec2 {
  x: number;
  y: number;
}

export class Parallax {
  private layers: ParallaxLayer[];
  private stage: HTMLElement;
  private enabled = true;
  private target: Vec2 = { x: 0, y: 0 };
  private current: Vec2 = { x: 0, y: 0 };
  private rafId: number | null = null;
  private pointerHandler: ((e: PointerEvent) => void) | null = null;
  private orientationHandler: ((e: DeviceOrientationEvent) => void) | null =
    null;
  private reducedMotion = false;
  private mq: MediaQueryList | null = null;
  private mqChangeHandler: ((e: MediaQueryListEvent) => void) | null = null;

  constructor(stage: HTMLElement, layers: ParallaxLayer[]) {
    this.stage = stage;
    this.layers = layers;

    // Respect prefers-reduced-motion
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.mq.matches;
      this.mqChangeHandler = (e: MediaQueryListEvent) => {
        this.reducedMotion = e.matches;
        if (this.reducedMotion) {
          this.resetTransforms();
        }
      };
      this.mq.addEventListener('change', this.mqChangeHandler);
    }
  }

  /** Attach pointermove + deviceorientation listeners and start the rAF loop. */
  start(): void {
    this.pointerHandler = (e: PointerEvent) => {
      if (!this.enabled || this.reducedMotion) return;
      const rect = this.stage.getBoundingClientRect();
      this.target.x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      this.target.y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    };
    this.stage.addEventListener('pointermove', this.pointerHandler);

    // Mobile: deviceorientation
    this.orientationHandler = (e: DeviceOrientationEvent) => {
      if (!this.enabled || this.reducedMotion) return;
      const gamma = e.gamma ?? 0; // left-right tilt [-90, 90]
      const beta = e.beta ?? 0; // front-back tilt [-180, 180]
      // Normalise to roughly [-0.5, 0.5]
      this.target.x = Math.max(-0.5, Math.min(0.5, gamma / 45));
      this.target.y = Math.max(-0.5, Math.min(0.5, (beta - 45) / 45));
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('deviceorientation', this.orientationHandler);
    }

    this.tick();
  }

  /** Detach listeners and cancel the rAF loop. */
  stop(): void {
    if (this.pointerHandler) {
      this.stage.removeEventListener('pointermove', this.pointerHandler);
      this.pointerHandler = null;
    }
    if (this.orientationHandler) {
      if (typeof window !== 'undefined') {
        window.removeEventListener('deviceorientation', this.orientationHandler);
      }
      this.orientationHandler = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /** Enable or disable parallax. When disabled, transforms are zeroed. */
  setEnabled(b: boolean): void {
    this.enabled = b;
    if (!b) {
      this.target = { x: 0, y: 0 };
      this.current = { x: 0, y: 0 };
      this.resetTransforms();
    }
  }

  /** Full teardown — stop + clear references. */
  destroy(): void {
    this.stop();
    if (this.mq && this.mqChangeHandler) {
      this.mq.removeEventListener('change', this.mqChangeHandler);
      this.mq = null;
      this.mqChangeHandler = null;
    }
    this.layers = [];
  }

  /**
   * Compute the expected transform for a given normalised offset and depth.
   * Exposed as a static helper for testing.
   */
  static computeTranslate(
    nx: number,
    ny: number,
    depth: number,
  ): { tx: number; ty: number } {
    return {
      tx: -nx * depth * MAX_PX,
      ty: -ny * depth * MAX_PX,
    };
  }

  // ── internal ──

  private tick = (): void => {
    if (!this.enabled || this.reducedMotion) {
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    // Lerp current toward target
    this.current.x += (this.target.x - this.current.x) * LERP;
    this.current.y += (this.target.y - this.current.y) * LERP;

    // Snap when close enough
    if (Math.abs(this.target.x - this.current.x) < SNAP) {
      this.current.x = this.target.x;
    }
    if (Math.abs(this.target.y - this.current.y) < SNAP) {
      this.current.y = this.target.y;
    }

    this.applyTransforms();
    this.rafId = requestAnimationFrame(this.tick);
  };

  private applyTransforms(): void {
    for (const layer of this.layers) {
      const { tx, ty } = Parallax.computeTranslate(
        this.current.x,
        this.current.y,
        layer.depth,
      );
      layer.el.style.transform = `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px)`;
    }
  }

  private resetTransforms(): void {
    for (const layer of this.layers) {
      layer.el.style.transform = '';
    }
  }
}
